import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_BASE_URL } from '../config';

function Settings({ isOpen, onClose }) {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [hideCode, setHideCode] = useState(user?.settings?.hide_code || false);

  // Update local state when user data changes
  useEffect(() => {
    setHideCode(user?.settings?.hide_code || false);
  }, [user?.settings?.hide_code]);

  const handleToggleHideCode = async (newValue) => {
    setHideCode(newValue);
    setLoading(true);
    setMessage('');
    
    try {
      const response = await axios.put(`${API_BASE_URL}/api/user/settings`, {
        settings: {
          hide_code: newValue
        }
      });

      if (response.data.success) {
        setMessage('Settings saved successfully!');
        // Refresh user data to get updated settings
        await refreshUser();
        
        // Clear message after 2 seconds
        setTimeout(() => setMessage(''), 2000);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage('Failed to save settings. Please try again.');
      // Revert the toggle on error
      setHideCode(!newValue);
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Code Display Settings */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Code Display</h3>
            
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <label htmlFor="hide-code-switch" className="text-sm font-medium text-gray-700">
                  Hide code cells
                </label>
                <p className="text-sm text-gray-500 mt-1">
                  When enabled, code cells will be hidden from view. This also hides "Code executed successfully" messages and error outputs.
                </p>
              </div>
              
              <div className="ml-4 flex items-center">
                {loading && (
                  <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                
                <button
                  id="hide-code-switch"
                  type="button"
                  onClick={() => handleToggleHideCode(!hideCode)}
                  disabled={loading}
                  className={`${
                    hideCode ? 'bg-blue-600' : 'bg-gray-200'
                  } ${
                    loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  } relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                >
                  <span
                    className={`${
                      hideCode ? 'translate-x-5' : 'translate-x-0'
                    } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Status Message */}
          {message && (
            <div className={`p-3 rounded-md text-sm ${
              message.includes('success') 
                ? 'bg-green-50 text-green-700 border border-green-200' 
                : 'bg-red-50 text-red-700 border border-red-200'
            }`}>
              {message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Settings;